import axios from 'axios'
import { SEARCH_URL } from '../config'

/**
 * A catalogue match from `tradeinsearch2`. The variant identity is `masterVariantId` (new
 * masterdata, C2S-754) — it flows into `PotentialCreate(masterVariantId)` alongside the
 * authoritative `ruleId`. `tradeInValue === 0` means the item is not eligible (UI disables Add).
 */
export interface SearchProduct {
  masterVariantId: string
  ruleId?: string
  tradeInValue: number
  /**
   * The currency `tradeInValue` is priced in — the endpoint echoes back the `currency` we asked for.
   * Kept with the match and handed to `PotentialCreate(currency)` so the created offer is priced off
   * the same currency's RRP as the figure the customer just saw.
   */
  currency?: string
  name: string
  color: string
  image: string
  /** All product images (primary at index 0). Falls back to `[image]` if the endpoint omits it. */
  images?: string[]
  category?: { id: string; name: string }
  /** Visual-search similarity 0..1 (1 = closest). Present only for image searches. */
  score?: number
  sizes: { id: string; size: string; sku: string; ean?: string }[]
}

/** Category facet for "Refine by category", flattened for the UI. */
export interface CategoryFacet {
  id: string
  name: string
  count: number
}

interface SearchResponse {
  items: SearchProduct[]
  facets: { category: { id: string; name: string }; count: number }[]
  paging: { page: number; pageSize: number; total: number; hasMore: boolean }
}

/** Ranked items plus the category facets that drive "Refine by category". */
export interface SearchResult {
  items: SearchProduct[]
  facets: CategoryFacet[]
}

function flattenFacets(res: SearchResponse | undefined): CategoryFacet[] {
  return (res?.facets ?? []).map((f) => ({ id: f.category.id, name: f.category.name, count: f.count }))
}

export interface CatalogueSearchParams {
  shopId: string
  query: string
  language?: string
  /**
   * Required, not optional: the endpoint falls back to a default currency of its own, and the
   * currency a match was priced in travels on to `PotentialCreate`. Resolve it with
   * `useSearchCurrency()` so the client — never the endpoint — decides.
   */
  currency: string
  /** Restrict to one category facet ("Refine by category"). */
  categoryId?: string
  /** 1-based page to fetch. The endpoint pages at 25/page (server hard-caps a query at 60 total). */
  page?: number
}

/** One page of ranked catalogue matches plus whether the server has further pages to offer. */
export interface CataloguePage {
  items: SearchProduct[]
  /** True when more pages exist beyond the one returned (drives the "Show more results" affordance). */
  hasMore: boolean
}

/**
 * Text search against `tradeinsearch2`:
 *   GET {SEARCH_URL}?shopId=<id>&search=<query>[&language&currency&categoryId&page]
 * Returns one page of the `{ items, facets, paging }` envelope: the ranked `items` plus the
 * `hasMore` flag so the caller can offer to load the next page. Called unauthenticated (no token
 * header), same as v1.
 */
export async function searchCatalogue(params: CatalogueSearchParams): Promise<CataloguePage> {
  const { shopId, query, language, currency, categoryId, page } = params
  const res = await axios.get<SearchResponse>(SEARCH_URL, {
    // The endpoint's refine param is `category` (see tradeInSearch2.ts).
    params: { shopId, search: query, language, currency, category: categoryId, page },
  })
  return { items: res.data?.items ?? [], hasMore: res.data?.paging?.hasMore ?? false }
}

export interface VisualSearchParams {
  shopId: string
  /** Publicly-accessible URL of the uploaded photo (from the photo receiver). */
  imageUrl: string
  language?: string
  /** Required for the same reason as on a text search — see {@link CatalogueSearchParams.currency}. */
  currency: string
  /** Restrict to one category facet ("Refine by category") — re-queries the server for its matches. */
  categoryId?: string
}

/**
 * Visual search against the SAME `tradeinsearch2` endpoint, passing `image` instead of `search`:
 *   GET {SEARCH_URL}?shopId=<id>&image=<publicUrl>[&category=<id>]
 * Returns trade-in-eligible matches ranked by `score`, plus category facets for refinement. Passing
 * `categoryId` narrows to that category server-side (the facet count spans the whole match set, not
 * just the first page, so refining must re-query rather than filter the loaded items).
 */
export async function searchByImage(params: VisualSearchParams): Promise<SearchResult> {
  const { shopId, imageUrl, language, currency, categoryId } = params
  const res = await axios.get<SearchResponse>(SEARCH_URL, {
    // Same refine param as text search: `category` (see tradeInSearch2.ts).
    params: { shopId, image: imageUrl, language, currency, category: categoryId },
  })
  return { items: res.data?.items ?? [], facets: flattenFacets(res.data) }
}
